import { createAsyncThunk } from "@reduxjs/toolkit"
import apiRequest from "../../lib/http"
import { extractApiError, normalizeError } from "../../utils/errorHelpers"

export const getPortalBranding = createAsyncThunk(
  'portal/getBranding',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get('/portal/branding')
      return { switchedOn: true, brand: response.data.data || null }
    } catch (error) {
      if (error?.response?.status === 404) return { switchedOn: false, brand: null }
      return rejectWithValue(normalizeError(error))
    }
  }
)

export const getPortalOverview = createAsyncThunk(
  'portal/getOverview',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get('/portal/overview')
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to fetch overview'
      return rejectWithValue(errorMessage)
    }
  }
)

export const getPortalPayments = createAsyncThunk(
  'portal/getPayments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get('/portal/payments')
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to fetch payments'
      return rejectWithValue(errorMessage)
    }
  }
)

export const getPortalAccounts = createAsyncThunk(
  'portal/getAccounts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get('/portal/accounts')
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to fetch accounts'
      return rejectWithValue(errorMessage)
    }
  }
)

export const getPortalDocuments = createAsyncThunk(
  'portal/getDocuments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get('/portal/documents')
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to fetch documents'
      return rejectWithValue(errorMessage)
    }
  }
)

export const getPortalActivity = createAsyncThunk(
  'portal/getActivity',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get('/portal/activity')
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to fetch activity'
      return rejectWithValue(errorMessage)
    }
  }
)

export const getPortalBudget = createAsyncThunk(
  'portal/getBudget',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get('/portal/budget')
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to fetch budget'
      return rejectWithValue(errorMessage)
    }
  }
)

export const submitPortalNote = createAsyncThunk(
  'portal/submitNote',
  async ({ text }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post('/portal/request-note', { text })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to submit note'
      return rejectWithValue(errorMessage)
    }
  }
)

export const getPortalNotes = createAsyncThunk(
  'portal/getNotes',
  async ({ offset = 0, limit = 20 } = {}, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get('/portal/notes', {
        params: { offset, limit },
      })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to fetch notes'
      return rejectWithValue(errorMessage)
    }
  }
)

export const getPortalPaymentMethods = createAsyncThunk(
  'portal/getPaymentMethods',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get('/portal/payment-methods')
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to fetch payment methods'
      return rejectWithValue(errorMessage)
    }
  }
)

export const addPortalBankAccount = createAsyncThunk(
  'portal/addBankAccount',
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post('/portal/payment-methods/bank', data)
      return response.data
    } catch (error) {
      return rejectWithValue(extractApiError(error, 'Failed to add bank account'))
    }
  }
)

export const addPortalCreditCard = createAsyncThunk(
  'portal/addCreditCard',
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post('/portal/payment-methods/card', data)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to add credit card'
      return rejectWithValue(errorMessage)
    }
  }
)
